import {
  ArrowRight,
  Building2,
  CalendarDays,
  Compass,
  Filter,
  Layers,
  ShieldCheck,
  Sparkles,
  TrendingUp,
  Users,
  UtensilsCrossed,
} from 'lucide-react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { AttributionFooter, ConfidenceBadge, LimitationsPanel } from '../components/Domain'
import { ChartProvenanceCallout } from '../components/ChartProvenanceCallout'
import { LeviPoiMap } from '../components/maps/LeviPoiMap'
import { AnalyticsInterestPanel } from '../components/panels/AnalyticsInterestPanel'
import { DestinationSignalsPanel } from '../components/panels/DestinationSignalsPanel'
import { RecommendationCard, WhatPanel } from '../components/panels/WhatPanel'
import { DemandChart, WhenPanel } from '../components/panels/WhenPanel'
import { MarketChart, WhoPanel } from '../components/panels/WhoPanel'
import { Alert, Badge, Button, Card, CardContent, CardHeader, CardTitle, NadiaLoader } from '../components/ui'
import { useApp } from '../context/AppContext'
import { askWhat, askWhen, askWho, getAnalyticsInterest } from '../lib/api'
import { useQueries, useQuery } from '@tanstack/react-query'
import type { Answer, POI, Question, Recommendation } from '../lib/types'
import { cn, pick } from '../lib/utils'

type SectorType = 'all' | 'accommodation' | 'activities' | 'gastronomy'
type TabType = 'all' | 'when' | 'who' | 'what'

// Curvas de estacionalidad por sector según config/synthetic/levi_season.yaml (12 meses, Ene-Dic)
const SECTOR_SEASON_CURVES: Record<Exclude<SectorType, 'all'>, number[]> = {
  accommodation: [1.45, 1.70, 1.40, 0.95, 0.62, 0.55, 0.70, 0.62, 0.58, 0.68, 0.95, 1.35],
  activities: [1.35, 1.55, 1.25, 0.80, 0.75, 0.95, 1.20, 1.15, 0.80, 0.70, 0.85, 1.20],
  gastronomy: [1.10, 1.20, 1.05, 0.90, 0.88, 1.00, 1.15, 1.10, 0.95, 0.90, 1.00, 1.15],
}

// Multiplicadores de afinidad de mercado por sector (basados en levi_season.yaml)
const SECTOR_MARKET_WEIGHTS: Record<Exclude<SectorType, 'all'>, Record<string, number>> = {
  accommodation: { FI: 1.15, SE: 1.10, NO: 1.10, GB: 0.95, DE: 0.95, NL: 0.90, CH: 0.90, AT: 0.85, FR: 0.85, PL: 0.80 },
  activities: { DE: 1.25, FR: 1.20, GB: 1.15, NL: 1.10, CH: 1.05, AT: 1.00, PL: 0.95, SE: 0.90, NO: 0.90, FI: 0.85 },
  gastronomy: { FI: 1.10, GB: 1.10, FR: 1.15, CH: 1.15, DE: 1.05, SE: 1.00, NO: 1.00, NL: 0.95, AT: 0.95, PL: 0.90 },
}

/**
 * Data Explorer: exploración y diagnóstico analítico unificado.
 * Reúne el resumen estratégico (KPIs + oportunidad) y el diagnóstico
 * WHEN / WHO / WHAT con filtro por sector, en una pantalla dedicada.
 */
export function DataExplorer() {
  const { t } = useTranslation()
  const { locale, audience } = useApp()
  const [activeTab, setActiveTab] = useState<TabType>('all')
  const [sectorFilter, setSectorFilter] = useState<SectorType>('all')
  const [selectedPoiId, setSelectedPoiId] = useState<string | null>(null)

  const results = useQueries({
    queries: [
      { queryKey: ['when', locale, audience], queryFn: () => askWhen({ locale, audience, horizon_months: 12 }), staleTime: 300_000 },
      { queryKey: ['who', locale, audience], queryFn: () => askWho({ locale, audience }), staleTime: 300_000 },
      { queryKey: ['what', locale, audience], queryFn: () => askWhat({ locale, audience, limit: 12 }), staleTime: 300_000 },
    ],
  })

  const interest = useQuery({
    queryKey: ['analytics-interest', locale],
    queryFn: () => getAnalyticsInterest(locale, 30),
    staleTime: 5 * 60_000,
  })

  const answers: Partial<Record<Question, Answer>> = {}
  ;(['when', 'who', 'what'] as const).forEach((q, i) => {
    if (results[i]?.data) answers[q] = results[i].data as Answer
  })

  const whenAns = answers.when
  const whoAns = answers.who
  const whatAns = answers.what

  const firstError = results.find((r) => r.isError)
  const anyLoading = results.some((r) => r.isLoading)

  // Filtrado de POIs / Recomendaciones por sector
  const filteredRecommendations = useMemo(() => {
    if (!whatAns?.recommendations) return []
    if (sectorFilter === 'all') return whatAns.recommendations

    return whatAns.recommendations.filter((rec: Recommendation) => {
      const ind = (rec.poi.industry || '').toLowerCase()
      const cat = (rec.poi.category || '').toLowerCase()
      const combined = `${ind} ${cat}`

      if (sectorFilter === 'accommodation') {
        return (
          combined.includes('chalet') ||
          combined.includes('hotel') ||
          combined.includes('lodging') ||
          combined.includes('alojamiento') ||
          combined.includes('cabin') ||
          combined.includes('resort')
        )
      }
      if (sectorFilter === 'activities') {
        return (
          combined.includes('safari') ||
          combined.includes('activity') ||
          combined.includes('attraction') ||
          combined.includes('sport') ||
          combined.includes('ski') ||
          combined.includes('tour')
        )
      }
      if (sectorFilter === 'gastronomy') {
        return (
          combined.includes('restaurant') ||
          combined.includes('bar') ||
          combined.includes('food') ||
          combined.includes('gastro') ||
          combined.includes('cafe')
        )
      }
      return true
    })
  }, [whatAns?.recommendations, sectorFilter])

  // Modulación dinámica de la curva de demanda según sector seleccionado
  const displayTimeline = useMemo(() => {
    if (!whenAns?.timeline) return []
    if (sectorFilter === 'all') return whenAns.timeline

    const curve = SECTOR_SEASON_CURVES[sectorFilter]
    if (!curve) return whenAns.timeline

    const avgCurve = curve.reduce((acc, v) => acc + v, 0) / curve.length
    return whenAns.timeline.map((point) => {
      const monthStr = point.period.split('-')[1]
      const monthIdx = monthStr ? (parseInt(monthStr, 10) - 1) % 12 : 0
      const factor = (curve[monthIdx] ?? 1.0) / avgCurve
      const modulatedVal = Math.min(1, Math.max(0.05, Number((point.value * factor).toFixed(3))))
      return {
        ...point,
        value: modulatedVal,
        raw: point.raw !== null ? Math.round(point.raw * factor) : null,
      }
    })
  }, [whenAns?.timeline, sectorFilter])

  // Modulación dinámica de cuotas de mercado según sector seleccionado
  const displayMarkets = useMemo(() => {
    if (!whoAns?.markets) return []
    if (sectorFilter === 'all') return whoAns.markets

    const affinity = SECTOR_MARKET_WEIGHTS[sectorFilter]
    if (!affinity) return whoAns.markets

    const weighted = whoAns.markets.map((m) => {
      const mult = affinity[m.country] ?? 1.0
      const newShare = (m.share ?? 0.05) * mult
      const newScore = Math.min(1, Math.max(0.1, Number((m.score * mult).toFixed(3))))
      return {
        ...m,
        share: newShare,
        score: newScore,
      }
    })

    const totalShare = weighted.reduce((acc, m) => acc + (m.share ?? 0), 0)
    const normalized = weighted.map((m) => ({
      ...m,
      share: totalShare > 0 ? Number(((m.share ?? 0) / totalShare).toFixed(4)) : m.share,
    }))

    return normalized.sort((a, b) => (b.share ?? 0) - (a.share ?? 0))
  }, [whoAns?.markets, sectorFilter])

  // KPIs sintéticos para Pymes sincronizados con el sector activo
  const optimalWindow = useMemo(() => {
    if (sectorFilter === 'all' || !displayTimeline.length) {
      return whenAns?.windows?.[0]
    }
    const maxPoint = [...displayTimeline].sort((a, b) => b.value - a.value)[0]
    return {
      ...(whenAns?.windows?.[0] ?? {
        id: 'modulated',
        months: [],
        score: maxPoint.value,
        rationale: {},
        contributions: [],
        confidence: 'high' as const,
        confidence_score: 0.9,
        dataset_ids: [],
        warnings: [],
      }),
      start: maxPoint.period,
      end: maxPoint.period,
      season: maxPoint.period,
    }
  }, [displayTimeline, whenAns?.windows, sectorFilter])

  const topMarket = displayMarkets[0] ?? whoAns?.markets?.[0]
  const topRecommendation = filteredRecommendations[0] ?? whatAns?.recommendations?.[0]

  return (
    <div className="space-y-10 sm:space-y-12">
      {firstError ? (
        <Alert variant="danger">
          <p>{t('common.error')}</p>
          <p className="mt-1 font-mono text-xs">
            {firstError.error instanceof Error ? firstError.error.message : ''}
          </p>
        </Alert>
      ) : null}

      {/* 3. EXPLORACIÓN Y DIAGNÓSTICO ANALÍTICO */}
      <section className="space-y-6">
        <div className="rounded-2xl border bg-card/80 p-5 sm:p-6 shadow-sm backdrop-blur-md space-y-5">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b pb-4">
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <span className="flex size-6 items-center justify-center rounded-md bg-primary/10 text-primary">
                  <Layers className="size-3.5" />
                </span>
                <h2 className="text-lg font-bold tracking-tight text-foreground">
                  {t('home.analyticsTitle', 'Exploración y Diagnóstico Analítico')}
                </h2>
              </div>
              <p className="text-xs text-muted-foreground">
                {t('home.analyticsSubtitle', 'Filtra por tu sector de actividad y navega en profundidad por cada dimensión del mercado.')}
              </p>
            </div>

            <div className="shrink-0 flex items-center gap-2">
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium bg-muted/80 text-muted-foreground border">
                <span className="size-1.5 rounded-full bg-emerald-500 animate-pulse" />
                {t('home.activeData', 'Datos activos de Levi')}
              </span>
            </div>
          </div>

          <div className="grid gap-4 lg:grid-cols-12 lg:items-center">
            <div className="lg:col-span-7 space-y-1.5">
              <span className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground/80 block">
                {t('home.perspective', 'Perspectiva de análisis')}:
              </span>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5 p-1 rounded-xl bg-muted/70 border border-border/50">
                <button
                  onClick={() => setActiveTab('all')}
                  className={cn(
                    'flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold transition-all duration-200 text-center',
                    activeTab === 'all'
                      ? 'bg-background text-foreground shadow-sm ring-1 ring-border/50'
                      : 'text-muted-foreground hover:text-foreground hover:bg-background/40'
                  )}
                >
                  <Layers className="size-3.5 text-primary shrink-0" />
                  <span className="truncate">{t('home.tabs.all')}</span>
                </button>
                <button
                  onClick={() => setActiveTab('when')}
                  className={cn(
                    'flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold transition-all duration-200 text-center',
                    activeTab === 'when'
                      ? 'bg-background text-foreground shadow-sm ring-1 ring-border/50'
                      : 'text-muted-foreground hover:text-foreground hover:bg-background/40'
                  )}
                >
                  <CalendarDays className="size-3.5 text-blue-500 shrink-0" />
                  <span className="truncate">{t('home.tabs.when')}</span>
                </button>
                <button
                  onClick={() => setActiveTab('who')}
                  className={cn(
                    'flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold transition-all duration-200 text-center',
                    activeTab === 'who'
                      ? 'bg-background text-foreground shadow-sm ring-1 ring-border/50'
                      : 'text-muted-foreground hover:text-foreground hover:bg-background/40'
                  )}
                >
                  <Users className="size-3.5 text-indigo-500 shrink-0" />
                  <span className="truncate">{t('home.tabs.who')}</span>
                </button>
                <button
                  onClick={() => setActiveTab('what')}
                  className={cn(
                    'flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold transition-all duration-200 text-center',
                    activeTab === 'what'
                      ? 'bg-background text-foreground shadow-sm ring-1 ring-border/50'
                      : 'text-muted-foreground hover:text-foreground hover:bg-background/40'
                  )}
                >
                  <Compass className="size-3.5 text-teal-500 shrink-0" />
                  <span className="truncate">{t('home.tabs.what')}</span>
                </button>
              </div>
            </div>

            <div className="lg:col-span-5 space-y-1.5">
              <span className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground/80 flex items-center gap-1.5">
                <Filter className="size-3 text-primary/80" />
                {t('home.sectorFilter')}
              </span>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5">
                {(
                  [
                    { id: 'all', label: t('home.sectors.all'), icon: null },
                    { id: 'accommodation', label: t('home.sectors.accommodation'), icon: Building2 },
                    { id: 'activities', label: t('home.sectors.activities'), icon: TrendingUp },
                    { id: 'gastronomy', label: t('home.sectors.gastronomy'), icon: UtensilsCrossed },
                  ] as const
                ).map(({ id, label, icon: Icon }) => {
                  const isSelected = sectorFilter === id
                  return (
                    <button
                      key={id}
                      onClick={() => setSectorFilter(id)}
                      className={cn(
                        'flex items-center justify-center gap-1 px-2.5 py-2 rounded-lg text-xs font-medium transition-all duration-200 border text-center',
                        isSelected
                          ? 'bg-primary text-primary-foreground border-primary shadow-sm shadow-primary/25 font-semibold'
                          : 'bg-background/80 text-muted-foreground border-border/60 hover:text-foreground hover:bg-accent/60'
                      )}
                    >
                      {Icon && <Icon className="size-3 shrink-0" />}
                      <span className="truncate">{label}</span>
                    </button>
                  )
                })}
              </div>
            </div>
          </div>
        </div>

        {activeTab === 'all' && (
          <div className="space-y-8">
            <div className="grid gap-6 lg:grid-cols-2">
              <Card className="border">
                <CardHeader className="pb-3 flex flex-row items-center justify-between">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <CardTitle className="text-base flex items-center gap-2">
                        <CalendarDays className="size-4 text-blue-500" />
                        <span>{t('question.when.title')}</span>
                      </CardTitle>
                      {sectorFilter !== 'all' && (
                        <Badge variant="secondary" className="text-[10px] px-2 py-0 h-5">
                          {t(`home.sectors.${sectorFilter}`)}
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {sectorFilter !== 'all'
                        ? `${t('home.sectorDemandNotice')} ${t(`home.sectors.${sectorFilter}`)}`
                        : pick(whenAns?.headline, locale) || t('question.when.help')}
                    </p>
                  </div>
                  <Link to="/when">
                    <Button variant="ghost" size="sm" className="text-xs gap-1">
                      {t('home.knowMore', 'Detalles')}
                      <ArrowRight className="size-3" />
                    </Button>
                  </Link>
                </CardHeader>
                <CardContent>
                  {displayTimeline.length > 0 ? (
                    <>
                      <DemandChart timeline={displayTimeline} height={220} />
                      <ChartProvenanceCallout
                        sourceName={t('chartExpl.whenDemand.sourceName')}
                        sourceType="mixed"
                        sourceDesc={t('chartExpl.whenDemand.sourceDesc')}
                        insight={t('chartExpl.whenDemand.insight')}
                      />
                    </>
                  ) : (
                    <NadiaLoader
                      message={t('loading.loadingDemand', 'Calculando curva de demanda y estacionalidad...')}
                      subtitle={t('loading.nadiaProcessing', 'Consultando modelos analíticos de Laponia...')}
                    />
                  )}
                </CardContent>
              </Card>

              <Card className="border">
                <CardHeader className="pb-3 flex flex-row items-center justify-between">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <CardTitle className="text-base flex items-center gap-2">
                        <Users className="size-4 text-indigo-500" />
                        <span>{t('question.who.title')}</span>
                      </CardTitle>
                      {sectorFilter !== 'all' && (
                        <Badge variant="secondary" className="text-[10px] px-2 py-0 h-5">
                          {t(`home.sectors.${sectorFilter}`)}
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {sectorFilter !== 'all'
                        ? `${t('home.sectorMarketNotice')} ${t(`home.sectors.${sectorFilter}`)}`
                        : pick(whoAns?.headline, locale) || t('question.who.help')}
                    </p>
                  </div>
                  <Link to="/who">
                    <Button variant="ghost" size="sm" className="text-xs gap-1">
                      {t('home.knowMore', 'Detalles')}
                      <ArrowRight className="size-3" />
                    </Button>
                  </Link>
                </CardHeader>
                <CardContent>
                  {displayMarkets.length > 0 ? (
                    <>
                      <MarketChart markets={displayMarkets} height={220} />
                      <ChartProvenanceCallout
                        sourceName={t('chartExpl.whoMarkets.sourceName')}
                        sourceType="real"
                        sourceDesc={t('chartExpl.whoMarkets.sourceDesc')}
                        insight={t('chartExpl.whoMarkets.insight')}
                      />
                    </>
                  ) : (
                    <NadiaLoader
                      message={t('loading.loadingMarkets', 'Cargando cuotas y mercados emisores...')}
                      subtitle={t('loading.nadiaProcessing', 'Consultando modelos analíticos de Laponia...')}
                    />
                  )}
                </CardContent>
              </Card>
            </div>

            <AnalyticsInterestPanel />

            <DestinationSignalsPanel />

            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-bold tracking-tight flex items-center gap-2">
                    <Compass className="size-5 text-teal-500" />
                    <span>{t('map.leviPoiTitle', 'Inteligencia de Oferta y Mapa Polar de Experiencias')}</span>
                  </h3>
                  <p className="text-xs text-muted-foreground">
                    {t('map.leviPoiSub', 'Cuadro analítico de oferta turística y mapa de orientación polar por zonas.')}
                  </p>
                </div>
                <Link to="/what">
                  <Button variant="outline" size="sm" className="text-xs gap-1.5">
                    {t('home.knowMore', 'Ver todos los POIs')}
                    <ArrowRight className="size-3.5" />
                  </Button>
                </Link>
              </div>

              {whatAns?.recommendations && whatAns.recommendations.length > 0 ? (
                <LeviPoiMap
                  recommendations={filteredRecommendations}
                  selectedPoiId={selectedPoiId}
                  onSelectPoi={(poi: POI) => setSelectedPoiId(poi.poi_id)}
                  poiTotal={whatAns?.poi_total}
                  poiSmeCount={whatAns?.poi_sme_count}
                />
              ) : (
                <NadiaLoader
                  minHeight="min-h-[360px]"
                  message={t('loading.loadingPois', 'Analizando catálogo de empresas y distribución espacial...')}
                  subtitle={t('loading.nadiaProcessing', 'Consultando modelos analíticos de Laponia...')}
                />
              )}

              <div className="grid gap-4 sm:grid-cols-2 2xl:grid-cols-4">
                {filteredRecommendations.slice(0, 4).map((rec, i) => (
                  <div
                    key={rec.poi.poi_id}
                    onClick={() => setSelectedPoiId(rec.poi.poi_id)}
                    className={cn(
                      'cursor-pointer transition-all',
                      selectedPoiId === rec.poi.poi_id ? 'ring-2 ring-primary rounded-xl' : ''
                    )}
                  >
                    <RecommendationCard rec={rec} rank={i + 1} interest={interest.data} />
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'when' && (
          <div className="space-y-6">
            <WhenPanel
              answer={whenAns ? { ...whenAns, timeline: displayTimeline } : undefined}
              loading={results[0].isLoading}
            />
          </div>
        )}

        {activeTab === 'who' && (
          <div className="space-y-6">
            <WhoPanel
              answer={whoAns ? { ...whoAns, markets: displayMarkets } : undefined}
              loading={results[1].isLoading}
            />
          </div>
        )}

        {activeTab === 'what' && (
          <div className="space-y-6">
            <WhatPanel
              answer={whatAns ? { ...whatAns, recommendations: filteredRecommendations } : undefined}
              loading={results[2].isLoading}
            />
          </div>
        )}
      </section>


      {/* 5. RESUMEN SEGÚN EL FILTRO SELECCIONADO */}
      {sectorFilter !== 'all' && (
        <>
          <div className="space-y-1">
            <h2 className="text-lg font-bold tracking-tight text-foreground">
              {t('explorer.summaryTitle', 'Resumen según tu selección')}
            </h2>
            <p className="text-xs text-muted-foreground">
              {t('explorer.summarySub', 'Oportunidad y KPIs del sector seleccionado')}: {t(`home.sectors.${sectorFilter}`)}
            </p>
          </div>
        {/* 1. GOLDEN INSIGHT CARD: Oportunidad Clave Detectada con CTA a Studio */}
        <section className="relative overflow-hidden rounded-2xl border border-primary/40 bg-gradient-to-br from-primary/10 via-card to-background p-6 shadow-md">
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
            <div className="space-y-2 max-w-2xl">
              <div className="flex items-center gap-2">
                <span className="flex size-7 items-center justify-center rounded-lg bg-primary text-primary-foreground">
                  <Sparkles className="size-4 animate-spin-slow" />
                </span>
                <h2 className="text-lg font-bold text-foreground tracking-tight">
                  {t('home.opportunityBadge', 'Oportunidad Clave Detectada')}
                </h2>
              </div>
              <p className="text-sm font-medium text-foreground/90 leading-relaxed">
                {whenAns && whoAns && whatAns ? (
                  <>
                  {t('home.insightLead', 'Anticípate en')}{' '}
                  <strong className="text-primary font-semibold">
                    {optimalWindow?.season
                      ? (t(`season.${optimalWindow.season}`) ?? optimalWindow.season)
                      : pick(whenAns.headline, locale) || t('home.insightNextWindow', 'la próxima ventana')}
                  </strong>{' '}
                  ({optimalWindow ? `${optimalWindow.start} → ${optimalWindow.end}` : ''}) {t('home.insightCapturing', 'captando la fuerte demanda de')}{' '}
                  <strong className="text-primary font-semibold">
                    {topMarket ? (pick(topMarket.name, locale) || topMarket.country) : t('home.insightPriorityMarkets', 'los mercados prioritarios')}
                  </strong>{' '}
                  {t('home.insightToPromote', 'para promocionar')}{' '}
                  <strong className="text-primary font-semibold">
                    {topRecommendation ? topRecommendation.poi.name : t('home.insightStandout', 'tus experiencias diferenciales')}
                  </strong>.
                </>
              ) : (
                t('home.insightAnalyzing', 'Analizando flujos de demanda en tiempo real...')
              )}
              </p>
              <p className="text-xs text-muted-foreground">
                {t('home.actionCtaSub', 'Convierte esta recomendación analítica directamente en un briefing creativo optimizado y listo para ejecutar.')}
              </p>
            </div>
  
            <div className="shrink-0 w-full sm:w-auto">
              <Link to="/studio">
                <Button size="lg" className="w-full sm:w-auto gap-2 shadow-lg shadow-primary/20 font-semibold text-sm">
                  <span>{t('home.actionCta', 'Crear y adaptar esta campaña en Campaign Studio')}</span>
                  <ArrowRight className="size-4" />
                </Button>
              </Link>
            </div>
          </div>
        </section>
        {/* 2. RESUMEN EJECUTIVO DE 4 KPIS ESTRATÉGICOS PARA PYMES */}
        <section className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {/* KPI 1: Ventana Óptima */}
          <Card className="border bg-card/60 backdrop-blur-sm">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between text-muted-foreground">
                <span className="text-xs font-medium uppercase tracking-wider">
                  {t('home.kpis.optimalWindow', 'Ventana Óptima')}
                </span>
                <CalendarDays className="size-4 text-blue-500" />
              </div>
            </CardHeader>
            <CardContent className="space-y-1">
              <p className="text-lg font-bold truncate text-foreground">
                {optimalWindow?.season
                  ? (t(`season.${optimalWindow.season}`) ?? optimalWindow.season)
                  : pick(whenAns?.headline, locale) || (anyLoading ? t('common.loading') : '—')}
              </p>
              <p className="text-xs text-muted-foreground">
                {optimalWindow ? `${optimalWindow.start} → ${optimalWindow.end}` : t('when.launchWindow', 'Ventana de campaña')}
              </p>
            </CardContent>
          </Card>
  
          {/* KPI 2: Mercado Prioritario */}
          <Card className="border bg-card/60 backdrop-blur-sm">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between text-muted-foreground">
                <span className="text-xs font-medium uppercase tracking-wider">
                  {t('home.kpis.topMarket', 'Mercado Prioritario')}
                </span>
                <Users className="size-4 text-indigo-500" />
              </div>
            </CardHeader>
            <CardContent className="space-y-1">
              <p className="text-lg font-bold truncate text-foreground">
                {topMarket ? (pick(topMarket.name, locale) || topMarket.country) : (anyLoading ? t('common.loading') : '—')}
              </p>
              <p className="text-xs text-muted-foreground">
                {topMarket?.recommended_budget_share
                  ? `${Math.round(topMarket.recommended_budget_share * 100)}% ${t('who.budget', 'presupuesto sugerido')}`
                  : t('who.leadingMarket', 'Cuota líder en pernoctaciones')}
              </p>
            </CardContent>
          </Card>
  
          {/* KPI 3: Experiencia Clave */}
          <Card className="border bg-card/60 backdrop-blur-sm">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between text-muted-foreground">
                <span className="text-xs font-medium uppercase tracking-wider">
                  {t('home.kpis.topExperience', 'Experiencia Clave')}
                </span>
                <Compass className="size-4 text-teal-500" />
              </div>
            </CardHeader>
            <CardContent className="space-y-1">
              <p className="text-lg font-bold truncate text-foreground">
                {topRecommendation?.poi.name || (anyLoading ? t('common.loading') : '—')}
              </p>
              <p className="text-xs text-muted-foreground truncate">
                {topRecommendation?.poi.industry || t('what.ranked', 'Puntuación y sentimiento')}
              </p>
            </CardContent>
          </Card>
  
          {/* KPI 4: Garantía ETDS */}
          <Card className="border bg-card/60 backdrop-blur-sm">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between text-muted-foreground">
                <span className="text-xs font-medium uppercase tracking-wider">
                  {t('common.confidence', 'Confianza')}
                </span>
                <ShieldCheck className="size-4 text-emerald-500" />
              </div>
            </CardHeader>
            <CardContent className="space-y-1">
              <div className="flex items-center gap-2">
                <p className="text-lg font-bold text-foreground">ETDS §13</p>
                {whenAns?.confidence && <ConfidenceBadge level={whenAns.confidence} />}
              </div>
              <p className="text-xs text-muted-foreground">
                {t('common.public', 'Pública agregada')}: k &ge; 5
              </p>
            </CardContent>
          </Card>
        </section>
        </>
      )}

      {/* 4. AVISOS DE LIMITACIONES Y ATRIBUCIÓN ETDS */}
      <section className="space-y-3 pt-4 border-t">
        {whenAns?.limitations && whenAns.limitations.length > 0 && (
          <LimitationsPanel
            limitations={whenAns.limitations}
            missingIndicators={whenAns.missing_indicators || []}
          />
        )}
        {whenAns && <AttributionFooter answer={whenAns} />}
        <p className="text-[11px] text-muted-foreground leading-relaxed">
          {t('home.etdsGuarantee', 'Garantía del Espacio Europeo de Datos de Turismo (ETDS): Conforme a política europea §13. Información agregada protegida, sin cesión de datos privados ni métricas individuales de competidores.')}
        </p>
      </section>
    </div>
  )
}
